import { Button, Table, TableContainer, TablePagination } from '@mui/material'
import type * as React from 'react'
import { ColumnSort, SortOrder, type Column, type RowData, DEFAULT_CHECKBOX_ID_COLUMN_NAME } from './DataTable.model'
import DataTableBody from './DataTableBody'
import DataTableHead from './DataTableHead'
import { useEffect, useState } from 'react'

export interface DataTableProps {
  headerColumns?: Column[]
  columns: Column[]
  rows: RowData[]
  name: string
  loading?: boolean
  sortable?: boolean
  initialColumnSorts?: ColumnSort[]
  hasCheckbox?: boolean
  checkboxIdColumnName?: string
  initialCheckedAll?: boolean
  initialCheckedItems?: any[]
  hoverShading?: boolean
  rowClickable?: boolean
  totalRecords?: number
  showGrandTotal?: boolean
  page: number

  onSortChange?: (column: Column, columnSorts: ColumnSort[]) => void
  onHeaderCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
  onRowCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean, row: RowData) => void
  onRowClick?: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData) => void
  onPageChange?: (event: React.MouseEvent<HTMLButtonElement> | null, page: number) => void
  onRowsPerPageChange?: (rowsPerPage: number) => void
}

export default function DataTable (props: DataTableProps): JSX.Element {
  const {
    columns, rows, loading, sortable, initialColumnSorts, hasCheckbox, checkboxIdColumnName, initialCheckedAll, initialCheckedItems,
    hoverShading, rowClickable, totalRecords, page,
    onSortChange, onHeaderCheckboxChange, onRowCheckboxChange, onRowClick, onPageChange, onRowsPerPageChange,
    ...rest
  } = props

  let colSort = initialColumnSorts
  const storedSort = window.localStorage.getItem(`${props.name}sort`)
  const storedPerPage = window.localStorage.getItem(`${props.name}rows`)
  if (storedSort !== null) {
    colSort = new Array<ColumnSort>(JSON.parse(storedSort))
  }

  const defaultRowsPerPage = 5
  const [allChecked, setAllChecked] = useState<boolean>(initialCheckedAll ?? false)
  const [checkedItems, setCheckedItems] = useState<any[]>(initialCheckedItems ?? [])
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>(colSort ?? [])
  const [rowsPerPage, setRowsPerPage] = useState<number>(Number(storedPerPage ?? defaultRowsPerPage))
  const [showReset, setShowReset] = useState<boolean>(storedSort !== null || storedPerPage !== null)
  useEffect(() => {
    if (hasCheckbox !== true) {
      setAllChecked(false)
      setCheckedItems([])
    }
  }, [hasCheckbox])
  useEffect(() => {
    if (storedPerPage !== null && props.onRowsPerPageChange !== undefined) {
      props.onRowsPerPageChange(Number(storedPerPage))
    }

    if (storedSort !== null && colSort !== undefined && onSortChange !== null) {
      const colItem = colSort[0]
      const col = columns.find(v => v.key === colItem.key)
      if (col !== undefined && onSortChange !== undefined) {
        onSortChange(col, colSort) // have the sort applied to the DataTable
      }
    }
  }, [])

  const _onSortHeaderClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>, column: Column): void => {
    let columnSort = columnSorts.find(v => v.key === column.key)
    if (columnSort == null) {
      columnSort = new ColumnSort(column.key)
    } else {
      columnSort = { ...columnSort, order: columnSort.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC }
    }
    window.localStorage.setItem(`${props.name}sort`, JSON.stringify(columnSort))
    setColumnSorts([columnSort])
    setShowReset(true)
    if (onSortChange != null) {
      onSortChange(column, [columnSort])
    }
  }

  const _onHeaderCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (event.target.checked) {
      setCheckedItems(rows.map(row => row[checkboxIdColumnName ?? DEFAULT_CHECKBOX_ID_COLUMN_NAME]))
    } else {
      setCheckedItems([])
    }
    setAllChecked(event.target.checked)
    if (onHeaderCheckboxChange != null) {
      onHeaderCheckboxChange(event, event.target.checked)
    }
  }

  const _onRowCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, row: RowData): void => {
    const tempCheckedItems = checkedItems.filter(v => v !== row[checkboxIdColumnName ?? DEFAULT_CHECKBOX_ID_COLUMN_NAME])
    if (event.target.checked) {
      tempCheckedItems.push(row[checkboxIdColumnName ?? DEFAULT_CHECKBOX_ID_COLUMN_NAME])
    }
    setAllChecked(tempCheckedItems.length === rows.length)
    setCheckedItems(tempCheckedItems)
    if (onRowCheckboxChange != null) {
      onRowCheckboxChange(event, event.target.checked, row)
    }
  }

  const _onRowClick = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>, row: RowData): void => {
    if (onRowClick != null) {
      onRowClick(event, row)
    }
  }

  const _onPageChange = (event: React.MouseEvent<HTMLButtonElement> | null, page: number): void => {
    if (onPageChange != null) {
      onPageChange(event, page)
    }
  }

  const _onRowsPerPageChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
    const newRowsPerPage = parseInt(event.target.value)
    window.localStorage.setItem(`${props.name}rows`, JSON.stringify(newRowsPerPage))
    setRowsPerPage(newRowsPerPage)
    setShowReset(true)
    if (onRowsPerPageChange != null) {
      onRowsPerPageChange(newRowsPerPage)
    }
  }

  const resetDisplay = (): void => {
    window.localStorage.removeItem(`${props.name}sort`)
    window.localStorage.removeItem(`${props.name}rows`)
    setShowReset(false)
    if (props.onRowsPerPageChange !== undefined && rowsPerPage !== defaultRowsPerPage) {
      props.onRowsPerPageChange(defaultRowsPerPage)
    }
    if (props.onSortChange !== undefined && columnSorts !== props.initialColumnSorts) {
      props.onSortChange(columns[0], props.initialColumnSorts ?? [])
    }
    setRowsPerPage(defaultRowsPerPage)
    setColumnSorts(props.initialColumnSorts ?? [])
  }

  return (
    <div style={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer>
        <Table size='medium' {...rest}>
          <DataTableHead
            key={props.name + 'head'}
            columns={columns}
            loading={loading}
            sortable={sortable}
            columnSorts={columnSorts}
            hasCheckbox={hasCheckbox}
            allChecked={allChecked}
            onHeaderCheckboxChange={_onHeaderCheckboxChange}
            onSortHeaderClick={_onSortHeaderClick}
          />
          <DataTableBody
            key={props.name + 'body'}
            columns={columns}
            rows={rows}
            loading={loading}
            hasCheckbox={hasCheckbox}
            checkboxIdColumnName={checkboxIdColumnName}
            checkedItems={checkedItems}
            hoverShading={hoverShading}
            rowClickable={rowClickable}
            onRowCheckboxChange={_onRowCheckboxChange}
            onRowClick={_onRowClick}
            showGrandTotal={props.showGrandTotal}
          />
        </Table>
      </TableContainer>
      {
        loading !== false
          ? null
          : <>
            {showReset && <span><Button
              size="small"
              variant="outlined"
              sx={{ color: '#595959', textTransform: 'none', marginTop: '10px' }}
              onClick={e => { resetDisplay() }}
              data-testid="resetDisplay"
            >
              Reset Display
            </Button>
            </span>}
            <TablePagination
              component='span'
              count={totalRecords ?? 0}
              rowsPerPageOptions={[5, 10, 25, 50]}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={_onPageChange}
              onRowsPerPageChange={_onRowsPerPageChange}
            />
          </>
      }
    </div>
  )
}
